import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Bug, RefreshCw, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { DeviceRegistrationService } from "@/services/deviceRegistrationService";
import { CapacitorUtils } from "@/utils/capacitor";
import { toast } from "@/hooks/use-toast";

interface NotificationDebugPanelProps {
  userId?: string;
}

const NotificationDebugPanel = ({ userId }: NotificationDebugPanelProps) => {
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const runDiagnostics = async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const info: any = {
        timestamp: new Date().toISOString(),
        browser: {
          notificationSupported: 'Notification' in window,
          serviceWorkerSupported: 'serviceWorker' in navigator,
          pushManagerSupported: 'PushManager' in window,
          permission: Notification.permission
        },
        platform: {
          isNative: CapacitorUtils.isNative(),
          platform: CapacitorUtils.getPlatform()
        }
      };

      // Test VAPID key retrieval
      try {
        const response = await fetch('https://miygybeltetsajkjsrwy.supabase.co/functions/v1/get-vapid-key', {
          headers: {
            'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1peWd5YmVsdGV0c2Fqa2pzcnd5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDgxMjAwNTQsImV4cCI6MjA2MzY5NjA1NH0.FM9A1y5eklgE6vofImQCqyp4NSh4iNiM1cJre3SJ20k',
            'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1peWd5YmVsdGV0c2Fqa2pzcnd5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDgxMjAwNTQsImV4cCI6MjA2MzY5NjA1NH0.FM9A1y5eklgE6vofImQCqyp4NSh4iNiM1cJre3SJ20k'
          }
        });
        info.vapidKey = {
          status: response.status,
          ok: response.ok,
          hasKey: response.ok ? 'Yes' : 'No'
        };
      } catch (error) {
        info.vapidKey = {
          error: error instanceof Error ? error.message : 'Unknown error'
        };
      }

      // Test service worker registration
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        info.serviceWorker = {
          registered: true,
          scope: registration.scope,
          state: registration.active?.state
        };
      } catch (error) {
        info.serviceWorker = {
          registered: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        };
      }

      // Test device info
      try {
        const deviceInfo = await DeviceRegistrationService.getCurrentDeviceInfo();
        info.device = {
          hasToken: !!deviceInfo.token,
          tokenLength: deviceInfo.token?.length || 0,
          type: deviceInfo.type,
          isNative: deviceInfo.isNative
        };
      } catch (error) {
        info.device = {
          error: error instanceof Error ? error.message : 'Unknown error'
        };
      }

      setDebugInfo(info);
    } catch (error) {
      toast({
        title: "Diagnostics failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: boolean | undefined) => {
    if (status === true) return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (status === false) return <XCircle className="w-4 h-4 text-red-500" />;
    return <AlertCircle className="w-4 h-4 text-yellow-500" />;
  };

  const getStatusBadge = (status: boolean | undefined) => {
    if (status === true) return <Badge variant="default" className="bg-green-500">OK</Badge>;
    if (status === false) return <Badge variant="destructive">Failed</Badge>;
    return <Badge variant="secondary">Unknown</Badge>;
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bug className="w-5 h-5" />
          Notification Diagnostics
        </CardTitle>
        <CardDescription>
          Debug notification system status and configuration
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button 
          onClick={runDiagnostics} 
          disabled={loading || !userId}
          className="w-full"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          {loading ? "Running Diagnostics..." : "Run Diagnostics"}
        </Button>

        {debugInfo && (
          <div className="space-y-4">
            <Separator />
            
            <div>
              <Label className="text-sm font-medium">Browser Support</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="flex items-center justify-between p-2 bg-muted rounded">
                  <span className="text-xs">Notifications</span>
                  {getStatusIcon(debugInfo.browser?.notificationSupported)}
                </div>
                <div className="flex items-center justify-between p-2 bg-muted rounded">
                  <span className="text-xs">Service Worker</span>
                  {getStatusIcon(debugInfo.browser?.serviceWorkerSupported)}
                </div>
                <div className="flex items-center justify-between p-2 bg-muted rounded">
                  <span className="text-xs">Push Manager</span>
                  {getStatusIcon(debugInfo.browser?.pushManagerSupported)}
                </div>
                <div className="flex items-center justify-between p-2 bg-muted rounded">
                  <span className="text-xs">Permission</span>
                  <Badge variant={debugInfo.browser?.permission === 'granted' ? 'default' : 'destructive'}>
                    {debugInfo.browser?.permission}
                  </Badge>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">VAPID Configuration</Label>
              <div className="p-2 bg-muted rounded mt-2">
                {debugInfo.vapidKey?.error ? (
                  <div className="flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-500" />
                    <span className="text-xs">Error: {debugInfo.vapidKey.error}</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="text-xs">VAPID Key</span>
                    {getStatusBadge(debugInfo.vapidKey?.ok)}
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Service Worker</Label>
              <div className="p-2 bg-muted rounded mt-2">
                {debugInfo.serviceWorker?.error ? (
                  <div className="flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-500" />
                    <span className="text-xs">Error: {debugInfo.serviceWorker.error}</span>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs">Registered</span>
                      {getStatusBadge(debugInfo.serviceWorker?.registered)}
                    </div>
                    {debugInfo.serviceWorker?.state && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs">State</span>
                        <Badge variant="secondary">{debugInfo.serviceWorker.state}</Badge>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Device Token</Label>
              <div className="p-2 bg-muted rounded mt-2">
                {debugInfo.device?.error ? (
                  <div className="flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-500" />
                    <span className="text-xs">Error: {debugInfo.device.error}</span>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs">Has Token</span>
                      {getStatusBadge(debugInfo.device?.hasToken)}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs">Token Length</span>
                      <Badge variant="secondary">{debugInfo.device?.tokenLength || 0}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs">Type</span>
                      <Badge variant="secondary">{debugInfo.device?.type}</Badge>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="text-xs text-muted-foreground">
              Last run: {new Date(debugInfo.timestamp).toLocaleString()}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationDebugPanel;